import { ArrowLeft, Users, Search, Settings, Mail, Edit, UserX, UserCheck, Download, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { CreateUserDialog } from '@/components/dialogs/CreateUserDialog';
import { EditUserDialog } from '@/components/dialogs/EditUserDialog';
import { useUsers, UserWithRole } from '@/hooks/useUsers';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { toast } from 'sonner';

const UserManagement = () => {
  const [editingUser, setEditingUser] = useState<UserWithRole | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  
  const { users, isLoading } = useUsers();
  const { hasPermission } = useRoleAccess();

  const exportUsersToCSV = () => {
    const headers = ['Name', 'Email', 'Role', 'Created', 'Updated'];
    const csvData = filteredUsers.map(user => [
      user.name || 'Unnamed',
      user.email,
      user.role || 'viewer',
      new Date(user.created_at).toLocaleDateString(),
      new Date(user.updated_at).toLocaleDateString()
    ]);
    
    const csv = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Users exported successfully');
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleBulkDelete = () => {
    if (selectedUsers.size === 0) return;
    toast.info(`Bulk delete for ${selectedUsers.size} users - Feature coming soon`);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-purple-500/20 text-purple-300';
      case 'admin': return 'bg-blue-500/20 text-blue-300';
      case 'manager': return 'bg-green-500/20 text-green-300';
      case 'staff': return 'bg-gray-500/20 text-gray-300';
      case 'viewer': return 'bg-slate-500/20 text-slate-300';
      default: return 'bg-gray-500/20 text-gray-300';
    }
  };

  const getInitials = (name: string) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeUsers = users.length;
  const totalUsers = users.length;

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="heading-section mb-2">User Management</h1>
              <p className="text-muted-foreground">Manage system users, roles, and permissions</p>
            </div>
            <CreateUserDialog />
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold">{totalUsers}</div>
                <div className="text-sm text-muted-foreground">Total Users</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{activeUsers}</div>
                <div className="text-sm text-muted-foreground">Active Users</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">
                  {users.filter(u => u.role === 'admin' || u.role === 'owner').length}
                </div>
                <div className="text-sm text-muted-foreground">Administrators</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">
                  {users.filter(u => u.role === 'manager').length}
                </div>
                <div className="text-sm text-muted-foreground">Managers</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="glass-panel border-0 mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input 
                    placeholder="Search users by name, email, or role..." 
                    className="glass-input pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="glass-button">
                  Filter by Role
                </Button>
                <Button variant="outline" className="glass-button">
                  Filter by Site
                </Button>
                <Button variant="outline" className="glass-button">
                  Filter by Status
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Users List */}
        <div className="space-y-4">
          {filteredUsers.length > 0 && (
            <div className="flex items-center gap-2 px-2">
              <Checkbox 
                checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                onCheckedChange={handleSelectAll}
              />
              <span className="text-sm text-muted-foreground">
                {selectedUsers.size > 0 ? `${selectedUsers.size} selected` : 'Select all'}
              </span>
            </div>
          )}
          {isLoading ? (
            <Card className="glass-panel border-0">
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading users...</p>
                </div>
              </CardContent>
            </Card>
          ) : filteredUsers.length === 0 ? (
            <Card className="glass-panel border-0">
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No users found</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            filteredUsers.map((user) => (
              <Card key={user.id} className="glass-panel border-0">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <Checkbox 
                        checked={selectedUsers.has(user.id)}
                        onCheckedChange={(checked) => handleSelectUser(user.id, checked as boolean)}
                      />
                      <Avatar className="w-12 h-12">
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {getInitials(user.name || user.email)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold">{user.name || 'Unnamed User'}</h3>
                          <Badge className={getRoleColor(user.role || 'viewer')}>
                            {user.role || 'viewer'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <Mail className="h-4 w-4" />
                          <span>{user.email}</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                          <div>
                            <span className="font-medium">Created:</span> {new Date(user.created_at).toLocaleDateString()}
                          </div>
                          <div>
                            <span className="font-medium">Updated:</span> {new Date(user.updated_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {hasPermission('canManageUsers') && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="glass-button"
                          onClick={() => setEditingUser(user)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        <EditUserDialog 
          user={editingUser}
          open={!!editingUser}
          onOpenChange={(open) => !open && setEditingUser(null)}
        />

        {/* Bulk Actions */}
        <Card className="glass-panel border-0 mt-8">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
                Showing {filteredUsers.length} of {users.length} users
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="glass-button"
                  onClick={exportUsersToCSV}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Users
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="glass-button"
                      disabled={selectedUsers.size === 0}
                    >
                      Bulk Actions ({selectedUsers.size})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="glass-panel border-0">
                    <DropdownMenuItem onClick={handleBulkDelete}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Selected
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserManagement;